import {
  NotificationItem,
  NotificationPayload,
} from "@/src/models/notification.model";

type PayloadCarrier = Pick<NotificationItem, "denormalizedPayload">;

/**
 * Parses a notification's `denormalizedPayload`, which arrives as a JSON
 * **string** rather than an object.
 *
 * Returns null for anything unusable instead of throwing, because the inbox is
 * shared with the platform: `Notifier/GetNotifications` returns Blocks' own
 * records alongside this app's. Those parse fine but carry a different
 * vocabulary — `{IsSuccess, title, description, projectKey}` for an
 * "Environment Data Migration" — with no `message`, `type` or actor.
 *
 * A null payload is also handled here: `JSON.parse(null)` does not throw, it
 * returns null, which is how such rows used to reach the UI with no warning at
 * all.
 */
export const parseNotificationPayload = (
  item: PayloadCarrier,
): NotificationPayload | null => {
  const raw = item.denormalizedPayload;
  if (typeof raw !== "string" || raw.trim() === "") return null;

  try {
    const parsed: unknown = JSON.parse(raw);
    return parsed !== null && typeof parsed === "object"
      ? (parsed as NotificationPayload)
      : null;
  } catch {
    return null;
  }
};

/**
 * The notification's display text, or null when there is none to show.
 *
 * `message` is the single field every row renders, and every one of this app's
 * senders sets it. Its absence is therefore the reliable marker of a record this
 * UI cannot present.
 *
 * Note what is deliberately NOT used as the marker: `payload.notificationType`.
 * Platform rows claim `"UserSpecificReceiverType"` while `payload.userId` is
 * empty, so that field cannot be trusted to separate them.
 */
export const getNotificationMessage = (item: PayloadCarrier): string | null => {
  const payload = parseNotificationPayload(item);
  if (!payload || !("message" in payload)) return null;
  // Follower rows are authorized at read time by the guarded reader. A legacy
  // message or realtime hint must never impersonate that trusted source.
  if ((payload as {type?:string}).type === "followed_author_posts" || (payload as {type?:string}).type === "follower_refresh") return null;

  const message: unknown = payload.message;
  return typeof message === "string" && message.trim() !== "" ? message : null;
};

/**
 * Whether a row has anything to show. Records that fail this render as an empty
 * card — blank avatar, blank text, just a timestamp — and cannot be dismissed,
 * because the platform exposes no per-user delete endpoint. Filtering them at
 * render is the only available remedy.
 */
export const isRenderableNotification = (item: PayloadCarrier): boolean =>
  getNotificationMessage(item) !== null;

/**
 * The stored actor name, with non-name tokens removed.
 *
 * Producers interpolate the current user into the name at creation time.
 * When that user was absent the literal "undefined undefined" got stored.
 * That string is truthy, so it used to yield the initial "U".
 * Returns "" when nothing usable is left, so callers can pick a fallback.
 */
export const cleanActorName = (actorName: string | undefined): string =>
  (actorName ?? "")
    .split(/\s+/)
    .filter((word) => word && !/^(undefined|null)$/i.test(word))
    .join(" ");

/** First letter of `actorName` for the avatar placeholder, or "?" when absent. */
export const getAvatarInitial = (actorName: string | undefined): string =>
  // `||` not `??`: "".charAt(0) is "", which is not nullish, so `?? "?"` never
  // fired and rows with no actor rendered an empty circle.
  cleanActorName(actorName).charAt(0).toUpperCase() || "?";

/**
 * Collapses repeated join requests from the same person for the same group down
 * to the most recent one.
 *
 * A request can be sent, cancelled and sent again, and each send is its own
 * notification, so a group admin ended up with a column of identical "X wants to
 * join Y" rows — every one of them pointing at a membership that no longer
 * exists except the last. The platform exposes no per-user delete (see
 * `isRenderableNotification`), so the superseded rows are dropped here at render
 * instead; callers also mark them read so they stop counting towards the badge.
 *
 * Newest wins: rows are keyed on actor + group and the later `createdTime`
 * replaces an earlier one, whichever order the server returned them in. Only
 * `join_request` rows are touched — every other type keeps its own history.
 */
export const collapseDuplicateJoinRequests = <T extends PayloadCarrier & Pick<NotificationItem, "createdTime">>(
  items: readonly T[],
): T[] => {
  const newestByRequest = new Map<string, T>();

  for (const item of items) {
    const payload = parseNotificationPayload(item);
    if (!payload || payload.type !== "join_request") continue;

    const key = `${payload.actorId ?? ""}|${payload.groupId ?? payload.targetEntityId ?? ""}`;
    const kept = newestByRequest.get(key);
    if (
      !kept ||
      new Date(item.createdTime).getTime() >= new Date(kept.createdTime).getTime()
    ) {
      newestByRequest.set(key, item);
    }
  }

  const keptRows = new Set(newestByRequest.values());

  return items.filter((item) => {
    const payload = parseNotificationPayload(item);
    if (!payload || payload.type !== "join_request") return true;
    return keptRows.has(item);
  });
};
